import { useConversationMessageCacheHandler } from "#features/conversations/hooks/useConversationMessageCacheHandler.ts";
import { useFileUploadOptionsStore } from "#features/conversations/stores/useFileUploadOptionsStore.ts";
import type { FileModel } from "#features/conversations/types/FileModel.ts";
import { Button, ButtonGroup, Flex, Text } from "@hopper-ui/components";
import { useCallback, useMemo } from "react";

interface Props {
    conversationId: string;
    messageId: string;
    files?: FileModel[] | null;
}

export const AssistantMessageFileUploadConfirmation = ({ conversationId, messageId, files }: Props) => {
    const { updateMessage } = useConversationMessageCacheHandler();
    const fileUploadOptions = useFileUploadOptionsStore(state => state.fileUploadOptions);

    const _files = useMemo(() => files?.filter(file => file.file).map(file => file.file!) ?? [], [files]);

    const handleConfirm = useCallback(() => {
        fileUploadOptions?.onConfirm(_files);
        updateMessage(conversationId, messageId, message => ({
            ...message,
            files: null
        }));
    }, [fileUploadOptions, _files, updateMessage, conversationId, messageId]);

    const handleCancel = useCallback(() => {
        updateMessage(conversationId, messageId, message => ({
            ...message,
            files: null
        }));
        fileUploadOptions?.onCancel?.();
    }, [conversationId, fileUploadOptions, messageId, updateMessage]);

    if (!fileUploadOptions || _files.length === 0) {
        return null;
    }

    const { message, confirmButtonLabel, cancelButtonLabel } = fileUploadOptions;

    return (
        <Flex direction="column" gap="inline-sm">
            <Text size="sm">{message}</Text>
            <ButtonGroup>
                <Button
                    variant="ghost-secondary"
                    UNSAFE_border="1px solid var(--hop-orchid-bloom-600)"
                    backgroundImage="linear-gradient(93deg, #F6F5FF 0%, #FFF 50%, #F6F5FF 100%)"
                    color="core_orchid-bloom-600"
                    colorHover="core_orchid-bloom-800"
                    colorFocus="core_orchid-bloom-800"
                    backgroundColorHover="core_orchid-bloom-600"
                    size="sm"
                    onPress={handleConfirm}
                >
                    {confirmButtonLabel}
                </Button>
                <Button
                    variant="ghost-secondary"
                    onPress={handleCancel}
                    size="sm"
                >
                    {cancelButtonLabel}
                </Button>
            </ButtonGroup>
        </Flex>
    );
};
